import React from 'react';
import cx from 'classnames';
import { makeStyles, useTheme } from '@material-ui/core';

import Img, { Props as ImgProps } from 'ha/ui/next/Img';
import { Breakpoint } from '@ha/dsl';
import buildContentfulAssetUrl from 'ha/utils/buildContentfulAssetUrl';
import buildContentfulAssetSrcSet from 'ha/utils/buildContentfulAssetSrcSet';

const useStyles = makeStyles({
  picture: {
    height: '100%',
    width: '100%',
  },
});

// array of width for browser to choose from
// most used display widths from https://www.websitedimensions.com/
const WIDTHS = [640, 1024, 1366, 1440, 1680, 1920];

interface Props {
  quality?: number;
  sizes?: { [key in Breakpoint | 'default']?: string };
  pictureProps?: React.HTMLAttributes<HTMLPictureElement>;
  format?: string;
}

const ContentfulImg: React.FC<Omit<ImgProps, 'sizes'> & Props> = ({
  src,
  quality = 70,
  sizes = {},
  format = 'jpg',
  pictureProps,
  ...rest
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { breakpoints } = theme;

  const defaultSrc = buildContentfulAssetUrl(src, {
    format,
    quality,
  });
  // if the url didn't change, it's not customizable with Contentful query params
  const isCustomizable = defaultSrc !== src;

  const currentBreakpoints = breakpoints.keys.filter(
    breakpoint => sizes[breakpoint],
  );
  const queries = currentBreakpoints.map((breakpoint, index) => {
    const minWidthQuery = `(min-width:${theme.breakpoints.values[breakpoint]}px)`;
    const nextBreakpoint = currentBreakpoints[index + 1];
    const mediaQuery = nextBreakpoint
      ? `${minWidthQuery} and (max-width:${theme.breakpoints.values[nextBreakpoint]}px)`
      : minWidthQuery;

    return `${mediaQuery} ${sizes[breakpoint]}`;
  });

  const sizesAttribute = [...queries, '100vw'].join(', ');

  return (
    <picture {...pictureProps} className={cx(classes.picture)}>
      {isCustomizable && (
        <source
          type="image/webp"
          sizes={sizesAttribute}
          srcSet={buildContentfulAssetSrcSet(src, WIDTHS, {
            format: 'webp',
            quality,
          })}
        />
      )}
      <Img
        role="presentation"
        src={defaultSrc}
        sizes={sizesAttribute}
        srcSet={
          isCustomizable
            ? buildContentfulAssetSrcSet(src, WIDTHS, {
                format,
                quality,
              })
            : undefined
        }
        {...rest}
      />
    </picture>
  );
};

export default ContentfulImg;
